import { DestroyRef, inject, Injectable, NgZone } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { JwtHelperService } from '@auth0/angular-jwt';
import { filter, fromEvent } from 'rxjs';

import { LocalStorageKey } from '#shared/enums/local-storage-key';

interface SessionTokenPayload {
    _id?: string;
    impersonatedBy?: string;
}

const jwtHelper = new JwtHelperService();

export function isSessionIdentitySwitch(event: Pick<StorageEvent, 'key' | 'oldValue' | 'newValue'>): boolean {
    if (event.key !== LocalStorageKey.JWT_TOKEN) {
        return false;
    }
    const previousIdentity = getSessionIdentity(event.oldValue);
    // A login after a logout reloads nothing: the logout already reloaded every tab that showed the previous identity.
    return previousIdentity !== null && previousIdentity !== getSessionIdentity(event.newValue);
}

// Identities are compared rather than raw tokens, so a same-user re-login keeps the tab's unsaved work.
function getSessionIdentity(token: string | null): string | null {
    if (!token) {
        return null;
    }
    try {
        const payload = jwtHelper.decodeToken<SessionTokenPayload>(token);
        return payload?._id ? `${payload._id}:${payload.impersonatedBy ?? ''}` : token;
    } catch {
        // A throw here would end the storage listener for the rest of the tab's life, so an undecodable token is compared as is.
        return token;
    }
}

@Injectable({ providedIn: 'root' })
export class SessionIdentitySyncService {
    private readonly _ngZone = inject(NgZone);

    reloadOnIdentitySwitchUntil(destroyRef: DestroyRef): void {
        // The JWT is shared by every tab through localStorage, so a tab left on the previous identity's page would act as the new one.
        // The storage event only fires in the other tabs: the tab that wrote the token never reloads itself.
        this._ngZone.runOutsideAngular(() => {
            fromEvent<StorageEvent>(window, 'storage')
                .pipe(filter(isSessionIdentitySwitch), takeUntilDestroyed(destroyRef))
                .subscribe(() => window.location.reload());
        });
    }
}
